import {
  decryptObject,
  getItemFromStorage,
  setItemToStorage,
  getDecryptPayload,
} from "./index";
import {
  adminPath,
  pathConstant,
  loginConstants,
  viewPathConstants,
  sidebarScreenConstants,
  featureFlagsEnums,
  emptyString,
  paramsConstants,
  screenPathsConstantsWithOutParams,
} from "../constants";
import {
  customerSurveyUrl,
  userPermissionsConstants,
} from "../views/shared-screens/constants";
import { isGroupAdmin, isManagerOrGroupAdmin, isSuperOrBrandAdmin } from "../shared";
import { ENV } from "../config/env";

const {
  BRAND_PROFILE,
  GROUP_PROFILE,
  MANAGER_PROFILE,
  STORE_PERFORMANCE,
  STORE_BRAND_PROFILE,
  BRAND_SURVEY_QUESTIONS,
  GROUP_SURVEY_QUESTIONS,
  MANAGER_SURVEY_QUESTIONS,
} = pathConstant;

const { PERFORMANCE, SURVEY_QUESTIONS, INBOX } = sidebarScreenConstants;
const { GROUP, BRAND_ADMIN, MANAGER } = userPermissionsConstants;
const {
  ROLE,
  BRAND_ID,
  GROUP_ID,
  ORDER_ID,
  MANAGER_ID,
  LOCATION_ID,
  CUSTOMER_ID,
  CONVERSATION_ID,
  RESTAURANT_EXTERNAL_ID,
} = loginConstants;

const {
  VERIFICATION_TOKEN,
  ENCRYPTED_PARAMETERS,
  LOCATION_ID: LOCATION_ID_QUERY_PARAM,
} = paramsConstants;
const {
  GROUP_INBOX_PATH,
  BRAND_INBOX_PATH,
  MANAGER_INBOX_PATH,
  BRAND_PROFILE_PATH,
  GROUP_PROFILE_PATH,
  MANAGER_PROFILE_PATH,
} = screenPathsConstantsWithOutParams;

//base routing depending upon the login type
export const authenticatedRouting = (props) => {
  const {
    ids,
    token,
    fromFR,
    pathname,
    navigate,
    confirmEmail,
    isAuthenticated,
    handleAdminToken,
    encryptedParamsObj,
    allBrandsWithFeatureFlags,
  } = props;

  // if this is FR routing then on every hard reload get the encrypted token from storage
  if (!token && isAuthenticated === false) {
    handleUnauthenticatedRouting(
      pathname,
      encryptedParamsObj,
      navigate,
      handleAdminToken,
      confirmEmail
    );
  }

  if (isAuthenticated) {
    navigateToDefaultScreen(
      ids,
      token,
      fromFR,
      pathname,
      navigate,
      handleAdminToken,
      allBrandsWithFeatureFlags
    );
  }
};

const handleUnauthenticatedRouting = (
  pathname,
  encryptedParamsObj,
  navigate,
  handleAdminToken,
  confirmEmail
) => {
  const sessionToken = getItemFromStorage(ENV.PAYLOAD);

  if (sessionToken) {
    const decodedSessionToken = decryptObject(sessionToken);
    handleAdminToken(decodedSessionToken.response);
    return;
  }

  const routes = {
    [customerSurveyUrl]: () => navigate(pathname, { replace: true }),
    [viewPathConstants.EMAIL_VERIFICATION]: () =>
      encryptedParamsObj.verificationToken &&
      navigate(
        `${viewPathConstants.EMAIL_VERIFICATION}?${VERIFICATION_TOKEN}=${encryptedParamsObj.verificationToken}`,
        { replace: true }
      ),
    [viewPathConstants.ADD_RECIPIENTS_SINGLE_RESPONSE]: () =>
      navigate(
        `${viewPathConstants.ADD_RECIPIENTS_SINGLE_RESPONSE}?${ENCRYPTED_PARAMETERS}=${encryptedParamsObj.encryptedBody}`,
        { replace: true }
      ),
    [viewPathConstants.ADD_RECIPIENTS_BULK_RESPONSE]: () =>
      navigate(
        `${viewPathConstants.ADD_RECIPIENTS_BULK_RESPONSE}?${ENCRYPTED_PARAMETERS}=${encryptedParamsObj.encryptedBody}`,
        { replace: true }
      ),
    [viewPathConstants.CONFIRM_PASSWORD]: () =>
      navigate(
        `${viewPathConstants.CONFIRM_PASSWORD}?${VERIFICATION_TOKEN}=${encryptedParamsObj.verificationToken}`,
        { replace: true }
      ),
    [viewPathConstants.LOGIN_EMAIL_CONFIRMATION]: () => {
      if (encryptedParamsObj.encryptedBody) {
        navigate(
          `${viewPathConstants.LOGIN}?${ENCRYPTED_PARAMETERS}=${encryptedParamsObj.encryptedBody}`,
          { replace: true }
        );
        confirmEmail(encryptedParamsObj.encryptedBody, navigate);
      }
    },
    [viewPathConstants.CHANGE_SURVEY_PREFERENCES]: () =>
      navigate(
        `${viewPathConstants.CHANGE_SURVEY_PREFERENCES}?${ENCRYPTED_PARAMETERS}=${encryptedParamsObj.encryptedBody}`,
        { replace: true }
      ),
  };

  for (const key in routes) {
    if (pathname.includes(key)) {
      routes[key]();
      return;
    }
  }

  navigate(viewPathConstants.LOGIN, { replace: true });
};

// Super-admin tools that Manager / Group Admin must not open by direct URL.
const SUPER_ADMIN_ONLY_PATHS = [
  adminPath.FEATURE_FLAGS,
  adminPath.SURVEY_QUEUES,
  adminPath.MERGE_DUPLICATE_STORES,
  adminPath.RESTAURANTS_USER_PERMISSIONS,
];

const isSuperAdminOnlyPath = (pathname) =>
  SUPER_ADMIN_ONLY_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

const navigateToDefaultScreen = (
  ids,
  token,
  fromFR,
  pathname,
  navigate,
  handleAdminToken,
  allBrandsWithFeatureFlags
) => {
  if (handleTokenRedirection(token, fromFR, ids, navigate, handleAdminToken))
    return;

  // Lower roles hitting admin-only URLs go to Unauthorized (with link home).
  if (
    isManagerOrGroupAdmin() &&
    isSuperAdminOnlyPath(pathname) &&
    pathname !== viewPathConstants.UNAUTHORIZED
  ) {
    navigate(viewPathConstants.UNAUTHORIZED, { replace: true });
    return;
  }

  if (pathname === viewPathConstants.ROOT || pathname === viewPathConstants.LOGIN) {
    handleDashboardNavigation(navigate, allBrandsWithFeatureFlags);
  }
};

// Used by the Unauthorized page "Go to Dashboard" action.
export const navigateToRoleDashboard = (navigate) => {
  const userRole = getItemFromStorage(ROLE);
  const storedToken = getDecryptPayload();
  navigateBasedOnUserRole(userRole, navigate, storedToken?.user);
};

const handleTokenRedirection = (
  token,
  fromFR,
  ids,
  navigate,
  handleAdminToken
) => {
  if (token && fromFR) {
    const decodedToken = window.atob(token);
    handleAdminToken(decodedToken);
    navigate(
      `${viewPathConstants.REDIRECTING_TO_STORE_PROFILE}?${LOCATION_ID_QUERY_PARAM}=${ids.locationId}`,
      { replace: true }
    );
    return true;
  }
  return false;
};

const handleDashboardNavigation = (navigate, allBrandsWithFeatureFlags) => {
  const userRole = getItemFromStorage(ROLE);
  const storedToken = getDecryptPayload();
  const { consumerLogin, user } = storedToken;
  const isInboxActiveForThisBrand = allBrandsWithFeatureFlags?.some(
    (brand) =>
      brand.brandId === consumerLogin?.brandId &&
      brand.stores?.some((store) =>
        store.storeFeatures?.includes(featureFlagsEnums.INBOX)
      )
  );

  if (
    handleConsumerLoginNavigation(
      consumerLogin,
      userRole,
      isInboxActiveForThisBrand,
      navigate
    )
  )
    return;
  navigateBasedOnUserRole(userRole, navigate, user);
};

const handleConsumerLoginNavigation = (
  consumerLogin,
  userRole,
  isInboxActiveForThisBrand,
  navigate
) => {
  if (!consumerLogin) return false;
  switch (consumerLogin.viewName) {
    case SURVEY_QUESTIONS:
    case PERFORMANCE:
      navigateToConsumerResponses(userRole, consumerLogin, navigate);
      return true;
    case INBOX:
      isInboxActiveForThisBrand
        ? navigateToSurveyInbox(userRole, consumerLogin, navigate)
        : navigateToInboxMessageScreen(navigate, consumerLogin);
      return true;
    default:
      return false;
  }
};

const navigateBasedOnUserRole = (userRole, navigate, user) => {
  switch (userRole) {
    case BRAND_ADMIN:
      navigate(viewPathConstants.STORES, { replace: true });
      break;
    case GROUP:
      navigate(`${viewPathConstants.DASHBOARD}/${user?.groupId}`, { replace: true });
      setItemToStorage(GROUP_ID, user?.groupId);
      break;
    case MANAGER:
      navigate(`${viewPathConstants.DASHBOARD}/${user?.id}`, { replace: true });
      setItemToStorage(MANAGER_ID, user?.id);
      break;
    default:
      navigate(viewPathConstants.STORES, { replace: true });
      break;
  }
};

const navigateToConsumerResponses = (
  role,
  {
    brandId,
    groupId,
    locationId,
    locationExternalId,
    customerId,
    managerId,
    viewName,
  },
  navigate
) => {
  let path = emptyString;
  let state = null;
  const consumerPath = `${viewPathConstants.CONSUMER_RESPONSES}/${customerId}`;

  // Set items to storage
  setItemToStorage(BRAND_ID, brandId);
  setItemToStorage(LOCATION_ID, locationId);
  setItemToStorage(RESTAURANT_EXTERNAL_ID, locationExternalId);

  // Check the role of the user and determine the path accordingly
  if (viewName === PERFORMANCE) {
    switch (role) {
      case BRAND_ADMIN:
        path = `${BRAND_PROFILE_PATH}/${brandId}/${STORE_PERFORMANCE}/${locationId}${consumerPath}`;
        state = { name: BRAND_PROFILE };
        break;
      case GROUP:
        path = `${GROUP_PROFILE_PATH}/${groupId}/${STORE_PERFORMANCE}/${locationId}${consumerPath}`;
        state = { name: GROUP_PROFILE };
        setItemToStorage(GROUP_ID, groupId);
        break;
      case MANAGER:
        path = `${MANAGER_PROFILE_PATH}/${managerId}/${STORE_PERFORMANCE}/${locationId}${consumerPath}`;
        state = { name: MANAGER_PROFILE };
        setItemToStorage(MANAGER_ID, managerId);
        break;
      default:
        path = `${BRAND_PROFILE_PATH}/${brandId}/${STORE_PERFORMANCE}/${locationId}${consumerPath}`;
        state = { name: STORE_BRAND_PROFILE };
        break;
    }
  } else if (viewName === SURVEY_QUESTIONS) {
    switch (role) {
      case BRAND_ADMIN:
        path = `${viewPathConstants.STORES}/${BRAND_SURVEY_QUESTIONS}/${brandId}`;
        break;
      case GROUP:
        path = `/${GROUP_SURVEY_QUESTIONS}/${groupId}`;
        setItemToStorage(GROUP_ID, groupId);
        setItemToStorage(BRAND_ID, groupId);
        break;
      case MANAGER:
        path = `/${MANAGER_SURVEY_QUESTIONS}/${managerId}`;
        setItemToStorage(MANAGER_ID, managerId);
        setItemToStorage(BRAND_ID, managerId);
        break;
      default:
        path = `${viewPathConstants.STORES}/${BRAND_SURVEY_QUESTIONS}/${brandId}`;
        break;
    }
  }

  // Perform the navigation with the constructed path and state
  navigate(path, { replace: true, state: state });
};

const navigateToSurveyInbox = (
  role,
  {
    orderId,
    brandId,
    groupId,
    managerId,
    locationId,
    conversationId,
    locationExternalId,
    conversationCustomerId,
  },
  navigate
) => {
  let path = emptyString;

  //Set items to storage
  setItemToStorage(ORDER_ID, orderId);
  setItemToStorage(BRAND_ID, brandId);
  setItemToStorage(LOCATION_ID, locationId);
  setItemToStorage(CONVERSATION_ID, conversationId);
  setItemToStorage(CUSTOMER_ID, conversationCustomerId);
  setItemToStorage(RESTAURANT_EXTERNAL_ID, locationExternalId);

  //Check the role of the user and determine the path accordingly
  switch (role) {
    case BRAND_ADMIN:
      path = `${BRAND_INBOX_PATH}/${brandId}`;
      break;
    case GROUP:
      path = `${GROUP_INBOX_PATH}/${groupId}`;
      setItemToStorage(GROUP_ID, groupId);
      break;
    case MANAGER:
      path = `${MANAGER_INBOX_PATH}/${managerId}`;
      setItemToStorage(MANAGER_ID, managerId);
      break;
    default:
      path = `${BRAND_INBOX_PATH}/${brandId}`;
      break;
  }

  //Perform the navigation with the constructed path and state
  navigate(path, { replace: true });
};

const navigateToInboxMessageScreen = (
  navigate,
  { brandId, groupId, managerId }
) => {
  if (isSuperOrBrandAdmin()) {
    setItemToStorage(BRAND_ID, brandId);
  } else if (isGroupAdmin()) {
    setItemToStorage(GROUP_ID, groupId);
  } else {
    setItemToStorage(MANAGER_ID, managerId);
  }
  navigate(viewPathConstants.REDIRECTING_TO_DEFAULT_SCREEN, {
    replace: true,
  });
};
